import type { ReactNode } from 'react';
import {
  Archive, ArrowUpCircle, Bell, BellOff, CircleMinus, Copy, CopyPlus, Download, Eye, ExternalLink,
  FolderInput, Link2, LogOut, PenLine, Pencil, Pin, PinOff, Plus, Power, PowerOff, RefreshCw, RotateCcw, Settings2, Undo2,
  SquareCheck, Trash2, Unlink, UserPlus, type LucideIcon,
} from 'lucide-react';
import type { LocaleDict } from '../../lib/i18n/types';

/**
 * The closed vocabulary of menu actions. One id is one icon, one label and one tone in every action menu
 * and context menu of the app, plugin menus included, so the same action is recognisable by its glyph
 * wherever it is offered. A menu item either names an entry here (`action`) or, for a genuinely
 * domain-specific action, brings its own icon; an item with neither is refused (`resolveMenuLook`).
 *
 * `specialised` marks the entries whose label may be extended for context ("Copy path", "Download as
 * HTML"); the icon and tone never change. Deleting a record is always `delete` (trash can, "Delete");
 * `remove` is only for taking something out of a list or setup while the thing itself stays.
 *
 * Adding an id means adding its `menuActions.<id>` label to all three dictionaries and its row to the
 * table in `docs/modules/web/components.md`.
 */
export const MENU_ACTIONS = {
  delete: { icon: Trash2, tone: 'danger' },
  remove: { icon: CircleMinus, tone: 'danger', specialised: true },
  edit: { icon: Pencil },
  replace: { icon: RefreshCw },
  rename: { icon: PenLine },
  duplicate: { icon: CopyPlus },
  copy: { icon: Copy, specialised: true },
  enable: { icon: Power },
  disable: { icon: PowerOff },
  archive: { icon: Archive },
  // Restore brings a record back from the archive, so it wears the arrow that turns back. `ArchiveRestore`
  // (a box with an up arrow) is about putting something INTO an archive, which is what the `archive` id one
  // line above already does, and `reset` beside it owns the circular arrow.
  restore: { icon: Undo2 },
  reset: { icon: RotateCcw },
  open: { icon: ExternalLink, specialised: true },
  view: { icon: Eye },
  move: { icon: FolderInput, specialised: true },
  pin: { icon: Pin },
  unpin: { icon: PinOff },
  mute: { icon: BellOff, specialised: true },
  unmute: { icon: Bell, specialised: true },
  download: { icon: Download, specialised: true },
  select: { icon: SquareCheck },
  add: { icon: Plus, specialised: true },
  settings: { icon: Settings2 },
  update: { icon: ArrowUpCircle },
  invite: { icon: UserPlus },
  leave: { icon: LogOut, tone: 'danger' },
  connect: { icon: Link2 },
  disconnect: { icon: Unlink, tone: 'danger' },
} as const satisfies Record<string, { icon: LucideIcon; tone?: 'danger'; specialised?: true }>;

export type MenuActionId = keyof typeof MENU_ACTIONS;

/** What identifies a menu row. Exactly one of: a vocabulary `action` (icon, tone and default label come
 *  from `MENU_ACTIONS`; `label` only extends it, and only where the entry is `specialised`), the row's own
 *  Lucide `icon` or pre-rendered `iconNode` for a domain-specific action, or `checked` for a row of a
 *  choice list, whose leading mark is its check. */
export type MenuItemLook =
  | { action: MenuActionId; label?: string; icon?: never; iconNode?: never }
  | { action?: never; label: string; icon: LucideIcon; iconNode?: never }
  | { action?: never; label: string; iconNode: ReactNode; icon?: never }
  | { action?: never; label: string; checked: boolean; icon?: LucideIcon; iconNode?: never };

export type ResolvedMenuLook = { label: string; icon?: LucideIcon; iconNode?: ReactNode; danger: boolean };

/** A menu row's final label, icon and tone. Throws on a row with no icon identity and on a label override
 *  of an entry that is not `specialised`: both are caller bugs, and a bundle builds its items at render
 *  time, so the failure shows in its first render rather than as a quiet inconsistency. */
export function resolveMenuLook(
  look: { action?: MenuActionId; label?: string; icon?: LucideIcon; iconNode?: ReactNode; checked?: boolean; tone?: 'default' | 'danger'; danger?: boolean },
  t: LocaleDict,
): ResolvedMenuLook {
  const danger = look.tone === 'danger' || look.danger === true;
  if (look.action !== undefined) {
    const entry: { icon: LucideIcon; tone?: 'danger'; specialised?: true } | undefined = MENU_ACTIONS[look.action];
    if (!entry) throw new Error(`Unknown menu action "${String(look.action)}"`);
    if (look.label !== undefined && !entry.specialised) throw new Error(`Menu action "${look.action}" keeps its label; only specialised actions may extend it`);
    return { label: look.label ?? t.menuActions[look.action], icon: entry.icon, danger: entry.tone === 'danger' || danger };
  }
  if (look.label === undefined) throw new Error('A menu item needs an action or a label');
  if (look.icon === undefined && look.iconNode === undefined && look.checked === undefined) {
    throw new Error(`Menu item "${look.label}" has no icon: name a menu action or pass an icon`);
  }
  return { label: look.label, icon: look.icon, iconNode: look.iconNode, danger };
}

/** Resolve every row of a menu entry list, recursing into submenus, so a row with no icon or a refused
 *  label fails when the menu renders rather than when the reader first opens it. `'divider'` entries pass. */
export function assertMenuEntries(entries: readonly ('divider' | Parameters<typeof resolveMenuLook>[0] & { items?: readonly unknown[] })[], t: LocaleDict): void {
  for (const entry of entries) {
    if (entry === 'divider') continue;
    resolveMenuLook(entry, t);
    if (entry.items) assertMenuEntries(entry.items as typeof entries, t);
  }
}
